<template>
  <organism-dashboard
    v-if="!isLoading"
    v-model="backupFilter"
    :latestConfiguration="latestConfiguration"
    :templateFields="templateFields"
    :templateList="templateList"
    :backupFields="backupFields"
    :backupList="backupList"
    @onClickCompareConfiguration="onClickCompareConfiguration"
    @onRequestDownloadAllLatestConfig="onRequestDownloadAllLatestConfig"
  ></organism-dashboard>
</template>

<script>
import { DateTime } from "luxon"
import {
  getConfigurationChangeList,
  getTemplateList,
  getNodeList,
  exportLatestConfiguration,
} from "@/api/api-module4"
import { mapActions, mapGetters } from "vuex"
import mixins from "@/mixins"
import OrganismDashboard from "@/components/organisms/networkConfigurationManagement/OrganismDashboard.vue"

export default {
  name: "Dashboard",
  components: { OrganismDashboard },
  mixins: [mixins],
  data() {
    return {
      latestConfiguration: null,
      intervalId: 0,
      templateFields: [
        {
          key: "template",
          label: "TEMPLATE NAME",
          thClass: "w-300px",
          tdClass: "w-300px",
        },
        {
          key: "description",
          label: "DESCRIPTION",
          thClass: "w-300px",
          tdClass: "w-300px",
        },
        {
          key: "compareIcon",
          label: "",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
      ],
      templateList: null,
      backupFields: [
        {
          key: "nodeName",
          label: "NODE NAME",
        },
        {
          key: "ipAddress",
          label: "IP ADDRESS",
        },
        {
          key: "pingStatus",
          label: "PING STATUS",
        },
        {
          key: "backupStatus",
          label: "BACKUP STATUS",
        },
      ],
      backupList: null,
      backupFilter: {
        perPage: 10,
        currentPage: 1,
        total: 0,
      },
    }
  },
  computed: {
    ...mapGetters("appAutoRefresh", ["getNCMDashboardAutoRefresh"]),

    isLoading() {
      return !this.latestConfiguration || !this.templateList || !this.backupList
    },

    isEnableAutoRefresh() {
      return this.getNCMDashboardAutoRefresh
    },
  },
  watch: {
    backupFilter: {
      deep: true,
      async handler() {
        await this.showLoading(true)
        await this.getBackupStatusFromApi()
        await this.showLoading(false)
      },
    },

    isEnableAutoRefresh: {
      deep: true,
      immediate: true,
      handler() {
        if (this.isEnableAutoRefresh) {
          this.intervalId = window.setInterval(() => {
            this.showLoading(true)
            this.getDataFromApi()
          }, 60000)
        } else {
          window.clearInterval(this.intervalId)
        }
      },
    },
  },
  async created() {
    this.setIsShowAutoRefresh(true)
    await this.getDataFromApi()
  },
  beforeDestroy() {
    this.setIsShowAutoRefresh(false)
    window.clearInterval(this.intervalId)
  },
  methods: {
    ...mapActions("appAutoRefresh", ["setIsShowAutoRefresh"]),

    async getDataFromApi() {
      await Promise.all([
        this.getLatestConfigurationFromApi(),
        this.getRecentlyUseTemplateFromApi(),
        this.getBackupStatusFromApi(),
      ])
    },

    async getLatestConfigurationFromApi() {
      this.showLoading(true)
      this.latestConfiguration = []

      await getConfigurationChangeList()
        .then((response) => {
          response.data.forEach((configuration) => {
            this.latestConfiguration.push({
              id: configuration.nodeId,
              nodeName: configuration.hostname,
              ipAddress: configuration.ipAddress,
              version: `Version ${configuration.version}`,
              date: DateTime.fromISO(configuration.timestamp).toFormat(
                "dd/MM/yyyy"
              ),
              time: DateTime.fromISO(configuration.timestamp).toFormat(
                "HH:mm:ss"
              ),
            })
          })
        })
        .finally(() => this.showLoading(false))
    },

    async getRecentlyUseTemplateFromApi() {
      this.showLoading(true)
      this.templateList = []

      const filter = {
        PageNumber: 1,
        PageSize: 3,
        OrderBy: "latestUsedAt desc",
      }

      await getTemplateList(filter)
        .then((response) => {
          if (response) {
            this.templateList = response.data.map((template) => {
              return {
                id: template.configTemplateId,
                template: {
                  name: template.configTemplateName,
                  tag: template.tags,
                },
                description: template.configTemplateDescription,
                latestUsedAt: template.latestUsedA,
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async getBackupStatusFromApi() {
      this.showLoading(true)
      this.backupList = []

      const filter = {
        PageNumber: this.backupFilter.currentPage || 1,
        PageSize: this.backupFilter.perPage || 10,
        OrderBy: "backupStatus asc",
      }

      await getNodeList(filter)
        .then((response) => {
          this.backupFilter.total = response.totalCount
          this.backupList = response.data.map((backup) => {
            return {
              id: backup.nodeId,
              nodeName: backup.hostname,
              ipAddress: backup.ipAddress,
              pingStatus: backup.nodeStatus,
              backupStatus: backup.backupStatus,
            }
          })
        })
        .finally(() => this.showLoading(false))
    },

    onClickCompareConfiguration(selectedNodeId, selectedConfigVersion) {
      if (selectedConfigVersion !== 1) {
        this.$router.replace({
          path: "/network-configuration/configuration-comparison",
          query: {
            BaselineNodeId: selectedNodeId,
            BaselineVersion: selectedConfigVersion - 1,
            CompareToNodeId: selectedNodeId,
            CompareToVersion: selectedConfigVersion,
          },
        })
      } else {
        this.$router.replace({
          path: "/network-configuration/configuration-comparison",
          query: {
            BaselineNodeId: selectedNodeId,
            BaselineVersion: selectedConfigVersion,
          },
        })
      }
    },

    onRequestDownloadAllLatestConfig() {
      this.sendRequestDownloadLatestConfigToApi()
    },

    async sendRequestDownloadLatestConfigToApi() {
      this.showLoading(true)
      await exportLatestConfiguration()
        .then((response) => {
          if (response) {
            this.toastSuccess("Download successfully")
          }
        })
        .catch(() => {
          this.toastError("Download failed")
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped></style>
